import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaTrash,
  FaCopy,
  FaThumbtack,
  FaPalette,
  FaFolderPlus,
  FaPlay,
  FaToggleOn,
  FaToggleOff,
  FaChevronDown,
} from "react-icons/fa";
import { useMultiSelect, SelectableItemType } from "@/hooks/use-multi-select";
import { MotionDiv } from "./motion-types";
import ColorPicker from "./color-picker";

/**
 * Action definition for the multi-select action bar
 */
interface Action {
  id: string;
  label: string;
  icon: React.ReactNode;
  danger?: boolean;
  onClick: () => void | Promise<void>;
}

interface MultiSelectActionBarProps {
  /** Handler for deleting selected dashboards */
  onDeleteDashboards?: (ids: string[]) => Promise<void>;
  /** Handler for duplicating selected dashboards */
  onDuplicateDashboards?: (ids: string[]) => Promise<void>;
  /** Handler for pinning selected dashboards */
  onPinDashboards?: (ids: string[], pinned: boolean) => Promise<void>;
  /** Handler for changing color of selected dashboards */
  onChangeDashboardsColor?: (ids: string[], color: string) => Promise<void>;
  /** Handler for moving dashboards to a group */
  onMoveDashboardsToGroup?: (ids: string[], groupId: string) => Promise<void>;

  /** Handler for deleting selected trackers */
  onDeleteTrackers?: (ids: string[]) => Promise<void>;
  /** Handler for activating selected trackers */
  onActivateTrackers?: (ids: string[]) => Promise<void>;
  /** Handler for deactivating selected trackers */
  onDeactivateTrackers?: (ids: string[]) => Promise<void>;
  /** Handler for running selected trackers */
  onRunTrackers?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected enrichments */
  onDeleteEnrichments?: (ids: string[]) => Promise<void>;
  /** Handler for activating selected enrichments */
  onActivateEnrichments?: (ids: string[]) => Promise<void>;
  /** Handler for deactivating selected enrichments */
  onDeactivateEnrichments?: (ids: string[]) => Promise<void>;
  /** Handler for running selected enrichments */
  onRunEnrichments?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected alerts */
  onDeleteAlerts?: (ids: string[]) => Promise<void>;
  /** Handler for activating selected alerts */
  onActivateAlerts?: (ids: string[]) => Promise<void>;
  /** Handler for deactivating selected alerts */
  onDeactivateAlerts?: (ids: string[]) => Promise<void>;
  /** Handler for running selected alerts */
  onRunAlerts?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected widgets */
  onDeleteWidgets?: (ids: string[]) => Promise<void>;

  /** Handler for deleting selected investigations */
  onDeleteInvestigations?: (ids: string[]) => Promise<void>;
}

/**
 * Multi-select action bar component.
 * Displays at the bottom of the sidebar when items are selected,
 * providing bulk actions based on the selected item type.
 */
const MultiSelectActionBar: React.FC<MultiSelectActionBarProps> = ({
  onDeleteDashboards,
  onDuplicateDashboards,
  onPinDashboards,
  onChangeDashboardsColor,
  onDeleteTrackers,
  onActivateTrackers,
  onDeactivateTrackers,
  onRunTrackers,
  onDeleteEnrichments,
  onActivateEnrichments,
  onDeactivateEnrichments,
  onRunEnrichments,
  onDeleteAlerts,
  onActivateAlerts,
  onDeactivateAlerts,
  onRunAlerts,
  onDeleteWidgets,
  onDeleteInvestigations,
}) => {
  const { selectedIds, itemType, selectionCount, clearSelection, hasSelection } =
    useMultiSelect();

  const [isLoading, setIsLoading] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  /**
   * Wrap an action handler with loading state and selection clearing
   */
  const wrapAction = useCallback(
    (handler?: (ids: string[]) => Promise<void>) => async () => {
      if (!handler || selectedIds.length === 0) return;

      setIsLoading(true);
      try {
        await handler(selectedIds);
        clearSelection();
      } catch (error) {
        console.error("Bulk action failed:", error);
      } finally {
        setIsLoading(false);
        setShowActionsMenu(false);
      }
    },
    [selectedIds, clearSelection]
  );

  /**
   * Handle color change for dashboards
   */
  const handleColorChange = useCallback(
    async (color: string) => {
      if (!onChangeDashboardsColor || selectedIds.length === 0) return;

      setIsLoading(true);
      try {
        await onChangeDashboardsColor(selectedIds, color);
        clearSelection();
      } catch (error) {
        console.error("Failed to change color:", error);
      } finally {
        setIsLoading(false);
        setShowColorPicker(false);
      }
    },
    [selectedIds, onChangeDashboardsColor, clearSelection]
  );

  /**
   * Get actions based on the selected item type
   */
  const getActions = useCallback((): Action[] => {
    switch (itemType) {
      case "dashboard":
        return [
          ...(onDuplicateDashboards
            ? [
                {
                  id: "duplicate",
                  label: "Dupliquer",
                  icon: <FaCopy size={12} />,
                  onClick: wrapAction(onDuplicateDashboards),
                },
              ]
            : []),
          ...(onPinDashboards
            ? [
                {
                  id: "pin",
                  label: "Épingler",
                  icon: <FaThumbtack size={12} />,
                  onClick: wrapAction((ids) => onPinDashboards(ids, true)),
                },
              ]
            : []),
          ...(onChangeDashboardsColor
            ? [
                {
                  id: "color",
                  label: "Couleur",
                  icon: <FaPalette size={12} />,
                  onClick: () => setShowColorPicker(true),
                },
              ]
            : []),
          ...(onDeleteDashboards
            ? [
                {
                  id: "delete",
                  label: "Supprimer",
                  icon: <FaTrash size={12} />,
                  danger: true,
                  onClick: wrapAction(onDeleteDashboards),
                },
              ]
            : []),
        ];

      case "tracker":
        return [
          ...(onRunTrackers
            ? [
                {
                  id: "run",
                  label: "Exécuter",
                  icon: <FaPlay size={12} />,
                  onClick: wrapAction(onRunTrackers),
                },
              ]
            : []),
          ...(onActivateTrackers
            ? [
                {
                  id: "activate",
                  label: "Activer",
                  icon: <FaToggleOn size={12} />,
                  onClick: wrapAction(onActivateTrackers),
                },
              ]
            : []),
          ...(onDeactivateTrackers
            ? [
                {
                  id: "deactivate",
                  label: "Désactiver",
                  icon: <FaToggleOff size={12} />,
                  onClick: wrapAction(onDeactivateTrackers),
                },
              ]
            : []),
          ...(onDeleteTrackers
            ? [
                {
                  id: "delete",
                  label: "Supprimer",
                  icon: <FaTrash size={12} />,
                  danger: true,
                  onClick: wrapAction(onDeleteTrackers),
                },
              ]
            : []),
        ];

      case "enrichment":
        return [
          ...(onRunEnrichments
            ? [
                {
                  id: "run",
                  label: "Exécuter",
                  icon: <FaPlay size={12} />,
                  onClick: wrapAction(onRunEnrichments),
                },
              ]
            : []),
          ...(onActivateEnrichments
            ? [
                {
                  id: "activate",
                  label: "Activer",
                  icon: <FaToggleOn size={12} />,
                  onClick: wrapAction(onActivateEnrichments),
                },
              ]
            : []),
          ...(onDeactivateEnrichments
            ? [
                {
                  id: "deactivate",
                  label: "Désactiver",
                  icon: <FaToggleOff size={12} />,
                  onClick: wrapAction(onDeactivateEnrichments),
                },
              ]
            : []),
          ...(onDeleteEnrichments
            ? [
                {
                  id: "delete",
                  label: "Supprimer",
                  icon: <FaTrash size={12} />,
                  danger: true,
                  onClick: wrapAction(onDeleteEnrichments),
                },
              ]
            : []),
        ];

      case "alert":
        return [
          ...(onRunAlerts
            ? [
                {
                  id: "run",
                  label: "Exécuter",
                  icon: <FaPlay size={12} />,
                  onClick: wrapAction(onRunAlerts),
                },
              ]
            : []),
          ...(onActivateAlerts
            ? [
                {
                  id: "activate",
                  label: "Activer",
                  icon: <FaToggleOn size={12} />,
                  onClick: wrapAction(onActivateAlerts),
                },
              ]
            : []),
          ...(onDeactivateAlerts
            ? [
                {
                  id: "deactivate",
                  label: "Désactiver",
                  icon: <FaToggleOff size={12} />,
                  onClick: wrapAction(onDeactivateAlerts),
                },
              ]
            : []),
          ...(onDeleteAlerts
            ? [
                {
                  id: "delete",
                  label: "Supprimer",
                  icon: <FaTrash size={12} />,
                  danger: true,
                  onClick: wrapAction(onDeleteAlerts),
                },
              ]
            : []),
        ];

      case "widget":
        return [
          ...(onDeleteWidgets
            ? [
                {
                  id: "delete",
                  label: "Supprimer",
                  icon: <FaTrash size={12} />,
                  danger: true,
                  onClick: wrapAction(onDeleteWidgets),
                },
              ]
            : []),
        ];

      case "investigation":
        return [
          ...(onDeleteInvestigations
            ? [
                {
                  id: "delete",
                  label: "Supprimer",
                  icon: <FaTrash size={12} />,
                  danger: true,
                  onClick: wrapAction(onDeleteInvestigations),
                },
              ]
            : []),
        ];

      default:
        return [];
    }
  }, [
    itemType,
    wrapAction,
    onDeleteDashboards,
    onDuplicateDashboards,
    onPinDashboards,
    onChangeDashboardsColor,
    onDeleteTrackers,
    onActivateTrackers,
    onDeactivateTrackers,
    onRunTrackers,
    onDeleteEnrichments,
    onActivateEnrichments,
    onDeactivateEnrichments,
    onRunEnrichments,
    onDeleteAlerts,
    onActivateAlerts,
    onDeactivateAlerts,
    onRunAlerts,
    onDeleteWidgets,
    onDeleteInvestigations,
  ]);

  const actions = getActions();

  // Get label for selected item type
  const getTypeLabel = (type: SelectableItemType | null): string => {
    switch (type) {
      case "dashboard":
        return selectionCount === 1 ? "dashboard" : "dashboards";
      case "tracker":
        return selectionCount === 1 ? "tracker" : "trackers";
      case "enrichment":
        return selectionCount === 1 ? "enrichment" : "enrichments";
      case "alert":
        return selectionCount === 1 ? "alerte" : "alertes";
      case "widget":
        return selectionCount === 1 ? "widget" : "widgets";
      case "investigation":
        return selectionCount === 1 ? "investigation" : "investigations";
      default:
        return selectionCount === 1 ? "élément" : "éléments";
    }
  };

  if (!hasSelection) {
    return null;
  }

  return (
    <AnimatePresence>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="absolute bottom-0 left-0 right-0 bg-[var(--arc-sidebar-background)] border-t border-[var(--arc-sidebar-border-accent)] shadow-lg z-50"
      >
        {/* Main action bar */}
        <div className="flex items-center justify-between px-3 py-2 gap-2">
          {/* Close button and selection count */}
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="p-1.5 rounded-md hover:bg-[var(--arc-sidebar-accent-glow)] text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] transition-colors"
              title="Annuler la sélection"
            >
              <FaTimes size={12} />
            </button>
            <span className="text-xs text-[var(--arc-sidebar-text-primary)] font-medium">
              {selectionCount} {getTypeLabel(itemType)}
            </span>
          </div>

          {/* Actions dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowActionsMenu(!showActionsMenu)}
              disabled={isLoading || actions.length === 0}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                isLoading || actions.length === 0
                  ? "bg-[var(--arc-sidebar-accent-glow)] text-[var(--arc-sidebar-text-secondary)] cursor-not-allowed"
                  : "bg-[var(--arc-accent)] text-white hover:bg-[var(--arc-accent-hover)]"
              }`}
            >
              {isLoading ? "..." : "Actions"}
              <FaChevronDown size={10} />
            </button>

            {/* Actions dropdown menu */}
            <AnimatePresence>
              {showActionsMenu && (
                <MotionDiv
                  initial={{ opacity: 0, scale: 0.95, y: 5 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 5 }}
                  transition={{ duration: 0.1 }}
                  className="absolute bottom-full right-0 mb-1 min-w-[160px] bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] py-1 overflow-hidden"
                >
                  {actions.map((action) => (
                    <button
                      key={action.id}
                      onClick={action.onClick}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                        action.danger
                          ? "text-red-400 hover:bg-red-500/20"
                          : "text-[var(--arc-sidebar-text-primary)] hover:bg-[var(--arc-sidebar-accent-glow)]"
                      }`}
                    >
                      {action.icon}
                      <span>{action.label}</span>
                    </button>
                  ))}
                </MotionDiv>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Color picker popup (for dashboards) */}
        <AnimatePresence>
          {showColorPicker && (
            <MotionDiv
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-full left-0 right-0 mb-1 mx-3 bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] p-3"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-[var(--arc-sidebar-text-primary)] font-medium">
                  Choisir une couleur
                </span>
                <button
                  onClick={() => setShowColorPicker(false)}
                  className="p-1 rounded hover:bg-[var(--arc-sidebar-accent-glow)] text-[var(--arc-sidebar-text-secondary)]"
                >
                  <FaTimes size={10} />
                </button>
              </div>
              <ColorPicker
                value="var(--arc-accent)"
                onChange={handleColorChange}
                compact
              />
            </MotionDiv>
          )}
        </AnimatePresence>
      </MotionDiv>
    </AnimatePresence>
  );
};

MultiSelectActionBar.displayName = "MultiSelectActionBar";

export default MultiSelectActionBar;
